import React, { useState } from 'react';
import { proService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { ProSheet, ProButton, IdentityPill, CheckGlyph, MUTED, LISTED_GRAY } from './proUi';

const MAX = 300;

/** Local wall-clock date + time → ISO, letting the browser apply its own offset. */
function localDateTime(dateStr, timeStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const [hh, mm] = String(timeStr || '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, hh || 0, mm || 0).toISOString();
}

const field = 'w-full border-2 border-black bg-white px-3 font-mono outline-none focus:ring-2 focus:ring-black focus:ring-offset-2';
const Label = ({ htmlFor, children, right }) => (
  <div className="flex items-center justify-between">
    <label htmlFor={htmlFor} className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em' }}>{children}</label>
    {right}
  </div>
);

/**
 * REQUEST TO BOOK on a listed page (handoff §7). Nothing is booked: the request is
 * stored against the page and the business is told. When they claim, it becomes an
 * offer. Logged out, the email mints a guest session the same way offers do.
 */
export default function RequestBookingModal({ slug, businessName, onClose, mode = 'book' }) {
  const title = mode === 'hire' ? 'HIRE THIS ROOM' : mode === 'private_event' ? 'ASK ABOUT A PRIVATE EVENT' : 'REQUEST TO BOOK';
  const { user, applyGuestSession } = useAuth();
  const { profile } = useUser();
  const loggedIn = !!user;

  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [budget, setBudget] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [whenError, setWhenError] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async () => {
    setError(''); setWhenError('');
    const cents = Math.round(parseFloat(budget || '0') * 100) || 0;
    if (!loggedIn && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Enter a valid email so they can answer you.'); return; }
    // A day with no hour can't be answered: every request names a date AND a start time (Nov 2 2026).
    const missing = !eventDate && !startTime ? 'Pick a date and a start time.'
      : !eventDate ? 'Pick a date for that start time.'
      : !startTime ? 'Add a start time for that date.'
      : endTime && endTime <= startTime ? 'The end time has to be after the start time.'
      : null;
    if (missing) { setWhenError(missing); return; }
    setBusy(true);
    const r = await proService.requestBooking(slug, {
      eventDate: eventDate ? localDateTime(eventDate, '00:00') : null,
      startsAt: eventDate && startTime ? localDateTime(eventDate, startTime) : null,
      endsAt: eventDate && endTime ? localDateTime(eventDate, endTime) : null,
      tzOffsetMinutes: new Date().getTimezoneOffset(),
      budgetCents: cents,
      message: (mode === 'book' ? '' : `[${title}] `) + (message.trim() || ''),
      email: loggedIn ? null : email.trim(),
      name: loggedIn ? null : (name.trim() || null),
    });
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not send the request.'); return; }
    if (r.data?.auth) applyGuestSession(r.data.auth);
    setSent(true);
  };

  if (sent) {
    return (
      <ProSheet width={560} title={title} onClose={onClose} testId="request-booking-modal"
        footer={<ProButton fill size="md" onClick={onClose}>DONE</ProButton>}>
        <div className="flex flex-col items-center text-center" style={{ padding: '32px 18px', gap: 14 }}>
          <div className="flex items-center justify-center border-2 border-black" style={{ width: 40, height: 40 }}><CheckGlyph size={20} /></div>
          <p className="font-mono font-bold uppercase" style={{ fontSize: 14, letterSpacing: '0.1em' }}>SENT. WE&apos;LL LET YOU KNOW WHEN THEY ANSWER.</p>
          <p className="font-mono" style={{ fontSize: 12, color: MUTED }}>Your request is stored against this page. If {businessName} joins Portal, it becomes a real offer you can pay through escrow.</p>
        </div>
      </ProSheet>
    );
  }

  return (
    <ProSheet width={560} title={title} onClose={busy ? undefined : onClose} testId="request-booking-modal"
      footer={(
        <>
          <ProButton size="md" onClick={onClose} disabled={busy}>CANCEL</ProButton>
          <ProButton fill size="md" onClick={submit} disabled={busy}>{busy ? 'SENDING…' : 'SEND REQUEST'}</ProButton>
        </>
      )}>
      <div className="flex flex-col" style={{ padding: 18, gap: 14 }}>
        {/* Context strip */}
        <div className="flex items-center gap-3 border-2 border-black" style={{ background: '#F5F5F5', padding: '10px 12px' }}>
          <IdentityPill listed />
          <span className="truncate font-mono font-bold" style={{ fontSize: 14 }}>{businessName}</span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rb-date">EVENT DATE</Label>
            <input id="rb-date" type="date" value={eventDate} onChange={(e) => { setEventDate(e.target.value); setWhenError(''); }} className={field} style={{ fontSize: 12, height: 40 }} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rb-start">START · END</Label>
            <div className="flex gap-2">
              <input id="rb-start" type="time" value={startTime} onChange={(e) => { setStartTime(e.target.value); setWhenError(''); }} className={field} style={{ fontSize: 12, height: 40 }} aria-label="Start time" />
              <input type="time" value={endTime} onChange={(e) => { setEndTime(e.target.value); setWhenError(''); }} className={field} style={{ fontSize: 12, height: 40 }} aria-label="End time" />
            </div>
          </div>
        </div>

        {whenError && <p className="font-mono font-bold uppercase" style={{ fontSize: 11, color: '#EF4444', letterSpacing: '0.05em', marginTop: -6 }} role="alert" data-testid="request-when-error">{whenError}</p>}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="rb-budget">BUDGET</Label>
          <div className="flex items-center border-2 border-black bg-white" style={{ height: 40 }}>
            <span className="border-r-2 border-black px-3 font-mono font-bold" style={{ fontSize: 12, lineHeight: '36px' }}>$</span>
            <input id="rb-budget" type="number" min="0" step="1" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="0.00"
              className="min-w-0 flex-1 bg-white px-3 font-mono outline-none" style={{ fontSize: 12 }} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="rb-msg" right={<span className="font-mono" style={{ fontSize: 10, color: LISTED_GRAY }}>{message.length} / {MAX}</span>}>{mode === 'hire' ? "WHAT'S THE NIGHT" : "WHAT'S THE EVENT"}</Label>
          <textarea id="rb-msg" rows={4} maxLength={MAX} value={message} onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
            placeholder="Who, how many, what kind of night." className={`${field} resize-none py-2`} style={{ fontSize: 13 }} />
        </div>

        {!loggedIn && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="logged-out-contact">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rb-email">YOUR EMAIL</Label>
              <input id="rb-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} style={{ fontSize: 12, height: 40 }} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rb-name">YOUR NAME</Label>
              <input id="rb-name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} className={field} style={{ fontSize: 12, height: 40 }} />
            </div>
          </div>
        )}
        {loggedIn && profile?.email && (
          <p className="font-mono" style={{ fontSize: 11, color: MUTED }}>They&apos;ll answer to {profile.email}.</p>
        )}

        {error && <p className="font-mono font-bold uppercase" style={{ fontSize: 11, color: '#EF4444', letterSpacing: '0.05em' }} role="alert">{error}</p>}

        <p className="border-t-2 border-black pt-3 font-mono" style={{ fontSize: 12 }}>
          We&apos;ll tell {businessName} by email and text. If they join, this becomes a real offer you can pay through Portal.
        </p>
      </div>
    </ProSheet>
  );
}
